import { BookOpen, ExternalLink, HelpCircle, MessageCircle, RotateCcw, Search } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { TourId } from '@/hooks/use-onboarding';
import { useOnboarding } from '@/hooks/use-onboarding';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { isBctStaff, useAuthStore } from '@/stores/auth';

// ============================================================================
// Types
// ============================================================================

interface HelpTopic {
  id: string;
  titleKey: string;
  contentKey: string;
  keywords: string[];
  context?: string[];
  articlePath?: string;
  staffOnly?: boolean;
}

// ============================================================================
// Help Topics
// ============================================================================

const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'issues-create',
    titleKey: 'topics.issuesCreate.title',
    contentKey: 'topics.issuesCreate.content',
    keywords: ['issue', 'create', 'new', 'bug', 'report'],
    context: ['issues', 'dashboard'],
    articlePath: 'customer-guide/creating-issues',
  },
  {
    id: 'issues-status',
    titleKey: 'topics.issuesStatus.title',
    contentKey: 'topics.issuesStatus.content',
    keywords: ['status', 'workflow', 'progress', 'resolved'],
    context: ['issues'],
    articlePath: 'issues-guide/issue-workflow',
  },
  {
    id: 'test-execution',
    titleKey: 'topics.testExecution.title',
    contentKey: 'topics.testExecution.content',
    keywords: ['test', 'execute', 'pass', 'fail', 'UAT', 'FAT'],
    context: ['test-execution', 'tests'],
    articlePath: 'customer-guide/test-execution',
  },
  {
    id: 'test-import',
    titleKey: 'topics.testImport.title',
    contentKey: 'topics.testImport.content',
    keywords: ['import', 'excel', 'csv', 'template', 'upload'],
    context: ['tests', 'test-plans'],
    articlePath: 'bct-guide/test-plans',
    staffOnly: true,
  },
  {
    id: 'documents-upload',
    titleKey: 'topics.documentsUpload.title',
    contentKey: 'topics.documentsUpload.content',
    keywords: ['upload', 'document', 'file', 'attachment'],
    context: ['documents'],
    articlePath: 'customer-guide/documents',
  },
  {
    id: 'tenant-switching',
    titleKey: 'topics.tenantSwitching.title',
    contentKey: 'topics.tenantSwitching.content',
    keywords: ['tenant', 'switch', 'customer', 'organization'],
    context: ['dashboard'],
    staffOnly: true,
  },
  {
    id: 'd365-sync',
    titleKey: 'topics.d365Sync.title',
    contentKey: 'topics.d365Sync.content',
    keywords: ['d365', 'dynamics', 'sync', 'integration'],
    context: ['projects', 'settings'],
    staffOnly: true,
  },
  {
    id: 'keyboard-shortcuts',
    titleKey: 'topics.keyboardShortcuts.title',
    contentKey: 'topics.keyboardShortcuts.content',
    keywords: ['keyboard', 'shortcut', 'hotkey'],
  },
  {
    id: 'go-live-checklists',
    titleKey: 'topics.goLiveChecklists.title',
    contentKey: 'topics.goLiveChecklists.content',
    keywords: ['go-live', 'checklist', 'evidence', 'sign-off'],
    context: ['checklist', 'go-live'],
    articlePath: 'go-live-guide/checklists',
  },
  {
    id: 'notifications',
    titleKey: 'topics.notifications.title',
    contentKey: 'topics.notifications.content',
    keywords: ['notification', 'alert', 'email', 'bell'],
    context: ['dashboard'],
    articlePath: 'customer-guide/notifications',
  },
  {
    id: 'issue-followup',
    titleKey: 'topics.issueFollowup.title',
    contentKey: 'topics.issueFollowup.content',
    keywords: ['follow', 'watch', 'track', 'update', 'comment'],
    context: ['issues', 'issue-detail'],
    articlePath: 'issues-guide/issue-collaboration',
  },
];

// ============================================================================
// Component
// ============================================================================

interface HelpPanelProps {
  currentContext?: string;
}

export function HelpPanel({ currentContext }: HelpPanelProps) {
  const { t } = useTranslation('help');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const isStaff = user?.role ? isBctStaff(user.role) : false;

  // Get onboarding hook for replay functionality
  const tourId = (currentContext as TourId) || 'dashboard';
  const { resetAllTours } = useOnboarding(tourId);

  /*
    No key of its own. The panel used to listen for ? on the document, alongside the
    shell's shortcuts dialog; both opened and each hid the other from assistive
    technology, so the reader got neither. The shell owns that key now and this panel is
    opened by its button.
  */

  // Filter and sort topics
  const filteredTopics = HELP_TOPICS.filter((topic) => {
    // Hide staff-only topics for customer users
    if (topic.staffOnly && !isStaff) return false;

    const matchesSearch =
      search === '' ||
      t(topic.titleKey).toLowerCase().includes(search.toLowerCase()) ||
      topic.keywords.some((k) => k.toLowerCase().includes(search.toLowerCase()));

    const matchesContext =
      !(currentContext && topic.context) || topic.context.includes(currentContext);

    // Show contextual topics first, then all if searching
    return search ? matchesSearch : matchesSearch && matchesContext;
  });

  // Sort: contextual topics first
  const sortedTopics = [...filteredTopics].sort((a, b) => {
    const aContextual = a.context?.includes(currentContext ?? '') ? 0 : 1;
    const bContextual = b.context?.includes(currentContext ?? '') ? 0 : 1;
    return aContextual - bContextual;
  });

  const handleRestartTour = useCallback(async () => {
    setOpen(false);
    await resetAllTours();
    toast({ title: t('panel.tourRestarted') });
  }, [resetAllTours, toast, t]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    if (value) {
      api
        .post('/analytics/onboarding', {
          eventType: 'help_search',
          eventData: { query: value },
        })
        .catch(() => {});
    }
  }, []);

  const handleTopicClick = useCallback((topicId: string) => {
    api
      .post('/analytics/onboarding', {
        eventType: 'help_topic_viewed',
        eventData: { topicId },
      })
      .catch(() => {});
  }, []);

  const handleOpenArticle = useCallback(
    (articlePath: string) => {
      setOpen(false);
      navigate(`/help#${articlePath}`);
    },
    [navigate],
  );

  const handleOpenHelpCenter = useCallback(() => {
    setOpen(false);
    navigate('/help');
  }, [navigate]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        api
          .post('/analytics/onboarding', {
            eventType: 'help_opened',
            eventData: { context: currentContext, source: 'button' },
          })
          .catch(() => {});
      }
    },
    [currentContext],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-inherit hover:text-inherit hover:bg-white/20"
          aria-label={t('panel.title')}
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {t('panel.title')}
          </SheetTitle>
          <SheetDescription className="sr-only">{t('description')}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('panel.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenHelpCenter}
              className="flex items-center gap-1.5"
            >
              <BookOpen className="h-3.5 w-3.5" />
              {t('panel.openHelpCenter')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestartTour}
              className="flex items-center gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('panel.replayTour')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('mailto:support@bct.nl', '_blank')}
              className="flex items-center gap-1.5"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              {t('panel.contactSupport')}
            </Button>
          </div>

          {/* Topics */}
          <ScrollArea className="h-[calc(100vh-220px)]">
            <div className="space-y-4 pr-4">
              {sortedTopics.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t('panel.noResults', { query: search })}
                </p>
              ) : (
                sortedTopics.map((topic) => (
                  <div
                    key={topic.id}
                    className="cursor-pointer space-y-2 rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
                    onClick={() => handleTopicClick(topic.id)}
                  >
                    <h3 className="text-sm font-medium">{t(topic.titleKey)}</h3>
                    <p className="whitespace-pre-line text-sm text-muted-foreground">
                      {t(topic.contentKey)}
                    </p>
                    {topic.articlePath && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenArticle(topic.articlePath!);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      >
                        {t('panel.readFullGuide')}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
